import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import { AiProcess } from '@flowx/designer/modules/new-ai/agents/analyst/services/analyst.service'
import {
  DocumentContextReference,
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  SendFeedbackPayload,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

export type CheckProcessRequest = {
  question: string
  context?: AiProcess | null
  session_id: string
  request_id: string
  // FlowX tenancy carried in the body so downstream services (ai-agents,
  // Observatory) can attribute the audit to the right project without
  // adding CORS-preflighted headers across every cluster ingress.
  application_id?: string
  workspace_id?: string
}

export type CheckUIRequest = {
  question: string
  context?: any | null
  session_id: string
  request_id: string
  application_id?: string
  workspace_id?: string
}

export type CheckProcessResponse = {
  error_message: string
  response: string
  type_of_action: string
}

export type CheckUIResponse = {
  error_message: string
  response: string
  type_of_action: string
}

export type ExtractComplianceFromDocumentResponse = {
  error_message: string
  response: string
  type_of_action: string
}

@Injectable({ providedIn: 'root' })
export class AiAuditorApi extends ApiHttpService {
  #auditor = `ai-engine/agents/auditor`

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#auditor}/initial`, payload)
  }

  checkProcess(payload: CheckProcessRequest): Observable<CheckProcessResponse> {
    return this.post(`${this.#auditor}/check/process/json`, payload)
  }

  checkUI(payload: CheckUIRequest): Observable<CheckUIResponse> {
    return this.post(`${this.#auditor}/check/ui/json`, payload)
  }

  extractComplianceFromDocument(
    documentContextReference: DocumentContextReference,
    context: any
  ): Observable<ExtractComplianceFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: check_name,
      type: type_of_action,
    } = documentContextReference

    return this.post(`${this.#auditor}/check/extract`, {
      collection_db,
      check_name,
      type_of_action,
      context,
    })
  }

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#auditor}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSECheckCompliance(
    payload: CheckProcessRequest | CheckUIRequest,
    action: 'check_process' | 'check_ui' = 'check_process'
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    // The /auditor/chat backend dispatches by `action` (see RequestChat in
    // agents/graphs/auditor/response.py). For UI audits the caller must pass
    // action="check_ui"; the default keeps existing call sites unchanged.
    return this.streamArray(`${this.#auditor}/chat`, { ...payload, action }).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#auditor}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }
}
